import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Mail,
  Phone,
  MapPin,
  FileText,
  ClipboardList,
  Receipt,
  Send,
  Edit,
  Trash2,
  Eye,
  Navigation,
  Clock,
  User,
  MessageSquare,
  Banknote,
  CreditCard,
  DollarSign,
  Printer,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("account");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [emailData, setEmailData] = useState({ recipient_email: "", subject: "" });
  const [sending, setSending] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);


  // Visit form state
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [visitData, setVisitData] = useState({ reason: "", comments: "" });
  const [savingVisit, setSavingVisit] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);

  // Payment receipt state
  const [receipts, setReceipts] = useState([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState({ amount: "", payment_method: "cash", bank: "", check_number: "", reference: "", notes: "" });
  const [savingReceipt, setSavingReceipt] = useState(false);

  // Print receipt state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [receiptToPrint, setReceiptToPrint] = useState(null);

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = async () => {
    try {
      const [clientRes, docsRes, visitsRes, receiptsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/documents?client_id=${clientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/visits?client_id=${clientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/receipts?client_id=${clientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setClient(clientRes.data);
      setDocuments(docsRes.data);
      setVisits(visitsRes.data);
      setReceipts(receiptsRes.data);
    } catch (error) {
      toast.error("Error al cargar datos del cliente");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setGettingLocation(false);
          toast.success("Ubicación obtenida correctamente");
        },
        (error) => {
          console.error("Error getting location:", error);
          setGettingLocation(false);
          toast.error("No se pudo obtener la ubicación");
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setGettingLocation(false);
      toast.error("Geolocalización no disponible en este dispositivo");
    }
  };

  const handleSaveVisit = async (e) => {
    e.preventDefault();
    if (!visitData.reason) {
      toast.error("Indica la razón de la visita");
      return;
    }

    setSavingVisit(true);
    try {
      await axios.post(
        `${API}/visits`,
        {
          client_id: clientId,
          reason: visitData.reason,
          comments: visitData.comments,
          latitude: currentLocation?.latitude,
          longitude: currentLocation?.longitude,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Visita registrada correctamente");
      setShowVisitModal(false);
      setVisitData({ reason: "", comments: "" });
      setCurrentLocation(null);
      fetchData();
    } catch (error) {
      toast.error("Error al registrar visita");
    } finally {
      setSavingVisit(false);
    }
  };

  const openVisitModal = () => {
    setVisitData({ reason: "", comments: "" });
    setCurrentLocation(null);
    setShowVisitModal(true);
    // Auto-get location when opening modal
    getCurrentLocation();
  };

  const handleSaveReceipt = async (e) => {
    e.preventDefault();
    if (!receiptData.amount || parseFloat(receiptData.amount) <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }

    setSavingReceipt(true);
    try {
      const response = await axios.post(
        `${API}/receipts`,
        {
          client_id: clientId,
          amount: parseFloat(receiptData.amount),
          payment_method: receiptData.payment_method,
          bank: receiptData.bank || null,
          check_number: receiptData.check_number || null,
          reference: receiptData.reference,
          notes: receiptData.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Recibo registrado correctamente");
      setShowReceiptModal(false);
      setReceiptData({ amount: "", payment_method: "cash", bank: "", check_number: "", reference: "", notes: "" });

      // Show print modal with the new receipt
      setReceiptToPrint({
        ...response.data,
        client_name: client?.name,
        client_rnc: client?.rnc,
      });
      setShowPrintModal(true);

      fetchData();
    } catch (error) {
      toast.error("Error al registrar recibo");
    } finally {
      setSavingReceipt(false);
    }
  };

  const handlePrintReceipt = (receipt) => {
    setReceiptToPrint({
      ...receipt,
      client_name: client?.name,
      client_rnc: client?.rnc,
    });
    setShowPrintModal(true);
  };

  const printTicket = () => {

    const ticketContent = document.getElementById('ticket-content').innerHTML;

    // Extraer todos los estilos del documento actual
    const styles = Array.from(document.styleSheets)
      .map(sheet => {
        try {
          return Array.from(sheet.cssRules)
            .map(rule => rule.cssText)
            .join('\n');
        } catch (e) {
          // Algunos estilos externos no permiten acceso por CORS
          return '';
        }
      })
      .join('\n');

    const contenido = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Recibo ${receiptToPrint?.receipt_number}</title>
        <style>
         ${styles}
          @page { margin: 0; size: 80mm auto; }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            margin: 0;
            padding: 8px;
            width: 76mm;
          }
          .ticket { width: 100%; }
          .center { text-align: center; }
          .right { text-align: right; }
          .bold { font-weight: bold; }
          .large { font-size: 14px; }
          .small { font-size: 10px; }
          .line { border-top: 1px dashed #000; margin: 8px 0; }
          .double-line { border-top: 2px solid #000; margin: 8px 0; }
          .row { display: flex; justify-content: space-between; margin: 4px 0; }
          .amount { font-size: 18px; font-weight: bold; }
          h1 { font-size: 16px; margin: 8px 0; }
          h2 { font-size: 14px; margin: 6px 0; }
          p { margin: 4px 0; }
        </style>
      </head>
      <body onload="window.print(); window.close();">
        ${ticketContent}
      </body>
      </html>
    `

    // ✅ Detectar si estamos en la app MiPedido
    if (window.ReactNativeWebView) {
      const datosRecibo = {
        empresa: user.company_name,
        logo: user.company_logo && user.company_logo.includes(',')
          ? user.company_logo.split(',')[1]
          : (user.company_logo || null),
        sucursal: "Portal de Ventas",
        nroRecibo: receiptToPrint?.receipt_number,
        cliente: receiptToPrint?.client_name,
        rnc: receiptToPrint?.client_rnc || "",
        fecha: receiptToPrint?.created_at ? new Date(receiptToPrint.created_at).toLocaleDateString("es-DO") : new Date().toLocaleDateString("es-DO"),
        hora: receiptToPrint?.created_at ? new Date(receiptToPrint.created_at).toLocaleTimeString("es-DO", { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString("es-DO", { hour: '2-digit', minute: '2-digit' }),
        metodo: getPaymentMethodLabel(receiptToPrint?.payment_method),
        banco: receiptToPrint?.bank || "",
        cheque: receiptToPrint?.check_number || "",
        monto: receiptToPrint?.amount || 0,
        notas: receiptToPrint?.notes || ""
      };

      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'print',
        format: 'recibo', // Identificador para que la app sepa qué plantilla usar
        datos: datosRecibo
      }));

      return; // No abrir ventana nueva
    }


    const printWindow = window.open('', '_blank', 'width=300,height=600');
    printWindow.document.write(contenido);
    printWindow.document.close();
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: "Efectivo",
      transfer: "Transferencia",
      check: "Cheque",
      card: "Tarjeta",
    };
    return labels[method] || method;
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(
        `${API}/send-email2`,
        {
          recipient_email: emailData.recipient_email,
          subject: emailData.subject,
          document_id: selectedDoc.id,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email enviado exitosamente");
      setShowEmailModal(false);
      setEmailData({ recipient_email: "", subject: "" });
    } catch (error) {
      toast.error("Error al enviar email: " + (error.response?.data?.detail || "Error desconocido"));
    } finally {
      setSending(false);
    }
  };

  const handleDeleteDoc = async () => {
    try {
      await axios.delete(`${API}/documents/${docToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Documento eliminado");
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar documento");
    }
  };

  const openEmailModal = (doc) => {
    setSelectedDoc(doc);
    const docTypeNames = { order: "Pedido", quote: "Cotización", invoice: "Factura" };
    setEmailData({
      recipient_email: client?.email || "",
      subject: `${docTypeNames[doc.doc_type]} #${doc.doc_number} - Portal de Ventas`,
    });
    setShowEmailModal(true);
  };

  const getDocsByType = (type) => documents.filter((d) => d.doc_type === type);

  const formatCurrency = (amount) =>
    `RD$ ${amount?.toLocaleString("es-DO", { minimumFractionDigits: 2 })}`;

  const getStatusBadge = (status) => {
    const styles = {
      pending: "status-pending",
      completed: "status-completed",
      cancelled: "status-cancelled",
    };
    const labels = {
      pending: "Pendiente",
      completed: "Completado",
      cancelled: "Cancelado",
    };
    return (
      <span className={`badge ${styles[status] || "status-pending"}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) return null;

  const DocumentTable = ({ docs, docType }) => (

    <div className="bg-white rounded border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">
          {docType === "order" && "Pedidos"}
          {docType === "quote" && "Cotizaciones"}
        </h3>
        <Button
          data-testid={`new-${docType}-btn`}
          onClick={() => navigate(`/document/new?client=${clientId}&type=${docType}&preciotype=${client.precio_asignated}`)}
          size="sm"
          className="bg-[#0F172A] hover:bg-[#1E293B]"
        >
          <Plus size={16} className="mr-1" />
          Nuevo s
        </Button>
      </div>


      {docs.length === 0 ? (
        <div className="p-8 text-center text-slate-500">
          No hay documentos de este tipo
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Número</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map((doc) => (
              <TableRow key={doc.id} className="table-row-hover">
                <TableCell className="font-medium mono">{doc.doc_number}</TableCell>
                <TableCell>{doc.created_at?.slice(0, 10)}</TableCell>
                <TableCell className="text-right mono">
                  {formatCurrency(parseFloat(doc.total))}
                </TableCell>
                <TableCell>{getStatusBadge(doc.status)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`view-doc-${doc.id}`}
                      onClick={() => navigate(`/document/${doc.id}?client=${doc.client_id}`)}
                      className="h-8 w-8"
                    >
                      <Eye size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`edit-doc-${doc.id}`}
                      onClick={() => navigate(`/document/${doc.id}?edit=true&client=${doc.client_id}`)}
                      className="h-8 w-8"
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`email-doc-${doc.id}`}
                      onClick={() => openEmailModal(doc)}
                      className="h-8 w-8 text-blue-600"
                    >
                      <Send size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-doc-${doc.id}`}
                      onClick={() => {
                        setDocToDelete(doc);
                        setShowDeleteDialog(true);
                      }}
                      className="h-8 w-8 text-red-600"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          data-testid="back-btn"
          onClick={() => navigate("/clients")}
        >
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-slate-800">{client.name}</h2>
          {client.rnc && <p className="text-sm text-slate-500 mono">RNC: {client.rnc}</p>}
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="register-receipt-btn"
            onClick={() => setShowReceiptModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <DollarSign size={18} className="mr-2" />
            Registrar Cobro
          </Button>
          <Button
            data-testid="register-visit-btn"
            onClick={openVisitModal}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Navigation size={18} className="mr-2" />
            Registrar Visita
          </Button>
        </div>
      </div>

      {/* Client Info Card */}
      <div className="bg-white rounded border border-slate-200 p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded">
              <Mail size={18} className="text-slate-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Email</p>
              <p className="text-sm text-slate-800">{client.email || "-"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded">
              <Phone size={18} className="text-slate-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Teléfono</p>
              <p className="text-sm text-slate-800">{client.phone || "-"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded">
              <MapPin size={18} className="text-slate-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Dirección</p>
              <p className="text-sm text-slate-800 truncate">{client.address || "-"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded">
              <Receipt size={18} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Balance</p>
              <p className={`text-sm font-semibold mono ${client.balance > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                {formatCurrency(client.balance)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger
            value="account"
            data-testid="tab-account"
            className="data-[state=active]:bg-white"
          >
            <Receipt size={16} className="mr-2" />
            Estado de Cuenta
          </TabsTrigger>
          <TabsTrigger
            value="orders"
            data-testid="tab-orders"
            className="data-[state=active]:bg-white"
          >
            <ClipboardList size={16} className="mr-2" />
            Pedidos ({getDocsByType("order").length})
          </TabsTrigger>
          <TabsTrigger
            value="quotes"
            data-testid="tab-quotes"
            className="data-[state=active]:bg-white"
          >
            <FileText size={16} className="mr-2" />
            Cotizaciones ({getDocsByType("quote").length})
          </TabsTrigger>

          <TabsTrigger
            value="receipts"
            data-testid="tab-receipts"
            className="data-[state=active]:bg-white"
          >
            <Banknote size={16} className="mr-2" />
            Recibos ({receipts.length})
          </TabsTrigger>
          <TabsTrigger
            value="visits"
            data-testid="tab-visits"
            className="data-[state=active]:bg-white"
          >
            <Navigation size={16} className="mr-2" />
            Visitas ({visits.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="account">
          <div className="bg-white rounded border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Estado de Cuenta
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-slate-50 rounded">
                <p className="text-sm text-slate-500">Total Facturado</p>
                <p className="text-2xl font-bold text-slate-800 mono">
                  {formatCurrency(
                    getDocsByType("invoice").filter((d) => d.status === "pending").reduce((sum, d) => sum + parseFloat(d.total), 0)
                  )}
                </p>
              </div>
              <div className="p-4 bg-amber-50 rounded">
                <p className="text-sm text-amber-700">Balance Pendiente</p>
                <p className="text-2xl font-bold text-amber-600 mono">
                  {formatCurrency(parseFloat(client.balance))}
                </p>
              </div>
              <div className="p-4 bg-emerald-50 rounded">
                <p className="text-sm text-emerald-700">Documentos Totales</p>
                <p className="text-2xl font-bold text-emerald-600">{documents.length}</p>
              </div>
            </div>

            {/* Recent Invoices */}
            <h4 className="font-medium text-slate-700 mb-3">Facturas Pendientes</h4>
            {getDocsByType("invoice").filter((d) => d.status === "pending").length === 0 ? (
              <p className="text-slate-500 text-sm">No hay facturas pendientes</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Factura</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getDocsByType("invoice")
                    .filter((d) => d.status === "pending")
                    .map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="mono">{doc.doc_number}</TableCell>
                        <TableCell>{doc.created_at?.slice(0, 10)}</TableCell>
                        <TableCell className="text-right mono">
                          {formatCurrency(parseFloat(doc.total))}
                        </TableCell>
                        <TableCell className="text-right mono">
                          {formatCurrency(parseFloat(doc.balance))}
                        </TableCell>
                        <TableCell>{getStatusBadge(doc.status)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <DocumentTable docs={getDocsByType("order")} docType="order" />
        </TabsContent>

        <TabsContent value="quotes">
          <DocumentTable docs={getDocsByType("quote")} docType="quote" />
        </TabsContent>



        <TabsContent value="receipts">
          <div className="bg-white rounded border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Recibos de Ingreso</h3>
              <Button
                data-testid="new-receipt-btn"
                onClick={() => setShowReceiptModal(true)}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus size={16} className="mr-1" />
                Nuevo Recibo
              </Button>
            </div>

            {receipts.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No hay recibos registrados
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recibo #</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead className="text-center">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receipts.map((receipt) => (
                    <TableRow key={receipt.id}>
                      <TableCell className="mono font-medium">{receipt.receipt_number}</TableCell>
                      <TableCell>{new Date(receipt.created_at).toLocaleDateString("es-DO")}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
                            {receipt.payment_method === "cash" && <Banknote size={12} />}
                            {receipt.payment_method === "transfer" && <CreditCard size={12} />}
                            {receipt.payment_method === "check" && <FileText size={12} />}
                            {receipt.payment_method === "card" && <CreditCard size={12} />}
                            {getPaymentMethodLabel(receipt.payment_method)}
                          </span>
                          {receipt.bank && (
                            <p className="text-xs text-slate-500">{receipt.bank}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="mono text-sm">
                        {receipt.check_number ? (
                          <span>Cheque #{receipt.check_number}</span>
                        ) : receipt.reference ? (
                          <span>{receipt.reference}</span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-right mono font-semibold text-emerald-600">
                        RD$ {parseFloat(receipt.amount)?.toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePrintReceipt(receipt)}
                          className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Printer size={16} className="mr-1" />
                          Imprimir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {receipts.length > 0 && (
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end">
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total Cobrado</p>
                  <p className="text-xl font-bold text-emerald-600 mono">
                    RD$ {receipts.reduce((sum, r) => sum + r.amount, 0).toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="visits">
          <div className="bg-white rounded border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Historial de Visitas</h3>
              <Button
                data-testid="new-visit-btn"
                onClick={openVisitModal}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus size={16} className="mr-1" />
                Nueva Visita
              </Button>
            </div>

            {visits.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No hay visitas registradas
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {visits.map((visit) => (
                  <div key={visit.id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                          <User size={16} className="text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{visit.user_name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock size={12} />
                            {new Date(visit.created_at).toLocaleString("es-DO")}
                          </p>
                        </div>
                      </div>
                      {visit.latitude && visit.longitude && (
                        <a
                          href={`https://www.google.com/maps?q=${visit.latitude},${visit.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <MapPin size={12} />
                          Ver ubicación
                        </a>
                      )}
                    </div>
                    <div className="ml-10">
                      <div className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium mb-2">
                        {visit.reason}
                      </div>
                      {visit.comments && (
                        <p className="text-sm text-slate-600 flex items-start gap-2">
                          <MessageSquare size={14} className="mt-0.5 text-slate-400" />
                          {visit.comments}
                        </p>
                      )}
                      {visit.latitude && (
                        <p className="text-xs text-slate-400 mt-2 mono">
                          GPS: {visit.latitude}, {visit.longitude}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar Documento por Email</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendEmail} className="space-y-4">
            <div className="space-y-2">
              <Label>Email del destinatario</Label>
              <Input
                type="email"
                data-testid="email-recipient-input"
                value={emailData.recipient_email}
                onChange={(e) =>
                  setEmailData({ ...emailData, recipient_email: e.target.value })
                }
                placeholder="correo@ejemplo.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Asunto</Label>
              <Input
                data-testid="email-subject-input"
                value={emailData.subject}
                onChange={(e) =>
                  setEmailData({ ...emailData, subject: e.target.value })
                }
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEmailModal(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="send-email-btn"
                disabled={sending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sending ? "Enviando..." : "Enviar Email"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El documento {docToDelete?.doc_number}{" "}
              será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={handleDeleteDoc}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign size={20} className="text-blue-600" />
              Registrar Cobro
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveReceipt} className="space-y-4">
            <div className="p-3 bg-slate-50 rounded border border-slate-200">
              <p className="text-sm text-slate-500">Cliente</p>
              <p className="font-medium text-slate-800">{client?.name}</p>
              <p className="text-xs text-slate-500 mt-1">
                Balance actual: <span className="font-semibold text-amber-600">RD$ {client?.balance?.toLocaleString("es-DO", { minimumFractionDigits: 2 })}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="receipt-amount">Monto (RD$) *</Label>
              <Input
                id="receipt-amount"
                data-testid="receipt-amount-input"
                type="number"
                min="0"
                step="0.01"
                value={receiptData.amount}
                onChange={(e) => setReceiptData({ ...receiptData, amount: e.target.value })}
                placeholder="0.00"
                className="text-lg mono"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="receipt-method">Método de Pago *</Label>
              <select
                id="receipt-method"
                data-testid="receipt-method-select"
                value={receiptData.payment_method}
                onChange={(e) => setReceiptData({ ...receiptData, payment_method: e.target.value, bank: "", check_number: "" })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="cash">💵 Efectivo</option>
                <option value="check">📄 Cheque</option>
              </select>
            </div>

            {/* Bank selector - shows for transfer and check */}
            {(receiptData.payment_method === "transfer" || receiptData.payment_method === "check") && (
              <div className="space-y-2">
                <Label htmlFor="receipt-bank">Banco *</Label>
                <select
                  id="receipt-bank"
                  data-testid="receipt-bank-select"
                  value={receiptData.bank}
                  onChange={(e) => setReceiptData({ ...receiptData, bank: e.target.value })}
                  className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Seleccionar banco...</option>
                  <option value="Banco Popular">Banco Popular</option>
                  <option value="Banco BHD León">Banco BHD León</option>
                  <option value="Banreservas">Banreservas</option>
                  <option value="Scotiabank">Scotiabank</option>
                  <option value="Banco Santa Cruz">Banco Santa Cruz</option>
                  <option value="Banco Promerica">Banco Promerica</option>
                  <option value="Banco López de Haro">Banco López de Haro</option>
                  <option value="Banco Caribe">Banco Caribe</option>
                  <option value="Banco Vimenca">Banco Vimenca</option>
                  <option value="Asociación Popular">Asociación Popular</option>
                  <option value="Asociación Cibao">Asociación Cibao</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
            )}

            {/* Check number - only shows for check payment */}
            {receiptData.payment_method === "check" && (
              <div className="space-y-2">
                <Label htmlFor="receipt-check-number">Número de Cheque *</Label>
                <Input
                  id="receipt-check-number"
                  data-testid="receipt-check-number-input"
                  value={receiptData.check_number}
                  onChange={(e) => setReceiptData({ ...receiptData, check_number: e.target.value })}
                  placeholder="Ej: 001234567"
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="receipt-reference">
                {receiptData.payment_method === "transfer" ? "Número de Referencia" : "Referencia (Opcional)"}
              </Label>
              <Input
                id="receipt-reference"
                data-testid="receipt-reference-input"
                value={receiptData.reference}
                onChange={(e) => setReceiptData({ ...receiptData, reference: e.target.value })}
                placeholder={receiptData.payment_method === "transfer" ? "Número de confirmación" : "Referencia adicional"}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="receipt-notes">Notas</Label>
              <textarea
                id="receipt-notes"
                data-testid="receipt-notes-input"
                value={receiptData.notes}
                onChange={(e) => setReceiptData({ ...receiptData, notes: e.target.value })}
                placeholder="Notas adicionales..."
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReceiptModal(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="save-receipt-btn"
                disabled={savingReceipt}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingReceipt ? "Guardando..." : "Guardar Recibo"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Visit Modal */}
      <Dialog open={showVisitModal} onOpenChange={setShowVisitModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation size={20} className="text-emerald-600" />
              Registrar Visita
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveVisit} className="space-y-4">
            <div className="p-3 bg-slate-50 rounded border border-slate-200">
              <p className="text-sm text-slate-500">Cliente</p>
              <p className="font-medium text-slate-800">{client?.name}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="visit-reason">Razón de la Visita *</Label>
              <select
                id="visit-reason"
                data-testid="visit-reason-select"
                value={visitData.reason}
                onChange={(e) => setVisitData({ ...visitData, reason: e.target.value })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              >
                <option value="">Seleccionar razón...</option>
                <option value="Seguimiento de pedido">Seguimiento de pedido</option>
                <option value="Cobro de factura">Cobro de factura</option>
                <option value="Presentación de productos">Presentación de productos</option>
                <option value="Cotización">Cotización</option>
                <option value="Entrega de mercancía">Entrega de mercancía</option>
                <option value="Reclamo o devolución">Reclamo o devolución</option>
                <option value="Visita de cortesía">Visita de cortesía</option>
                <option value="Otro">Otro</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="visit-comments">Comentarios</Label>
              <textarea
                id="visit-comments"
                data-testid="visit-comments-input"
                value={visitData.comments}
                onChange={(e) => setVisitData({ ...visitData, comments: e.target.value })}
                placeholder="Detalles adicionales de la visita..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
            </div>

            {/* GPS Location */}
            <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-emerald-800 flex items-center gap-2">
                  <MapPin size={16} />
                  Ubicación GPS
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="get-location-btn"
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  className="h-7 text-xs"
                >
                  {gettingLocation ? (
                    <>
                      <span className="animate-spin mr-1">⏳</span>
                      Obteniendo...
                    </>
                  ) : (
                    <>
                      <Navigation size={12} className="mr-1" />
                      {currentLocation ? "Actualizar" : "Obtener"}
                    </>
                  )}
                </Button>
              </div>
              {currentLocation ? (
                <div className="text-xs text-emerald-700 mono">
                  <p>Lat: {currentLocation.latitude.toFixed(6)}</p>
                  <p>Lng: {currentLocation.longitude.toFixed(6)}</p>
                  <a
                    href={`https://www.google.com/maps?q=${currentLocation.latitude},${currentLocation.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline mt-1 inline-block"
                  >
                    Ver en mapa →
                  </a>
                </div>
              ) : (
                <p className="text-xs text-emerald-600">
                  {gettingLocation ? "Obteniendo ubicación..." : "Ubicación no capturada aún"}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowVisitModal(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="save-visit-btn"
                disabled={savingVisit}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {savingVisit ? "Guardando..." : "Guardar Visita"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Print Receipt Modal - Ticket Format */}
      <Dialog open={showPrintModal} onOpenChange={setShowPrintModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check size={20} className="text-emerald-600" />
              ¡Recibo Guardado!
            </DialogTitle>
          </DialogHeader>

          {/* Ticket Preview */}
          <div className="bg-white border-2 border-dashed border-slate-300 rounded-lg p-4 max-h-[400px] overflow-auto">
            <div id="ticket-content" className="font-mono text-[10px] w-[250px] bg-white text-black">

              {/* HEADER */}
              <div className="center">
                <div className="bold large">{user.company_name}</div>
                <div className="small">Portal de Ventas</div>
                <div className="line"></div>
                <div className="bold large">RECIBO PROVISIONAL</div>
                <div className="bold">#{receiptToPrint?.receipt_number}</div>
              </div>

              <div className="double-line"></div>

              {/* CLIENTE */}
              <div className="left">
                <div className="bold underline">CLIENTE:</div>
                <div>{receiptToPrint?.client_name}</div>
                {receiptToPrint?.client_rnc && (
                  <div>RNC: {receiptToPrint?.client_rnc}</div>
                )}
              </div>

              <div className="line"></div>

              {/* SECCIÓN DE DATOS: ETIQUETA (IZQ) --- DATO (DER) */}
              {/* Usamos una estructura que el parser entienda como líneas independientes con alineación */}

              <div className="flex-row">
                <span className="left">Fecha:</span>
                <span className="right">{receiptToPrint?.created_at ? new Date(receiptToPrint.created_at).toLocaleDateString("es-DO") : new Date().toLocaleDateString("es-DO")}</span>
              </div>

              <div className="flex-row">
                <span className="left">Hora:</span>
                <span className="right">{receiptToPrint?.created_at ? new Date(receiptToPrint.created_at).toLocaleTimeString("es-DO", { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString("es-DO", { hour: '2-digit', minute: '2-digit' })}</span>
              </div>

              <div className="flex-row">
                <span className="left">Metodo:</span>
                <span className="right bold">{getPaymentMethodLabel(receiptToPrint?.payment_method)}</span>
              </div>

              {receiptToPrint?.bank && (
                <div className="flex-row">
                  <span className="left">Banco:</span>
                  <span className="right">{receiptToPrint?.bank}</span>
                </div>
              )}

              {receiptToPrint?.check_number && (
                <div className="flex-row">
                  <span className="left">Cheque #:</span>
                  <span className="right bold">{receiptToPrint?.check_number}</span>
                </div>
              )}

              <div className="double-line"></div>

              {/* MONTO RECIBIDO */}
              <div className="center">
                <div className="small">MONTO RECIBIDO</div>
                <div className="bold large">
                  RD$ {receiptToPrint?.amount?.toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div className="line"></div>

              {/* NOTAS */}
              {receiptToPrint?.notes && (
                <div className="left">
                  <div className="bold">Notas:</div>
                  <div>{receiptToPrint?.notes}</div>
                  <div className="line"></div>
                </div>
              )}

              {/* FOOTER */}
              <div className="center small">
                <div>Gracias por su pago</div>
                <div>Este recibo es su comprobante</div>
                <div>de pago provisional. Conservelo.</div>
                <div className="line"></div>
                <div className="bold">*** FIN DEL RECIBO ***</div>
              </div>

              {/* Limpieza de buffer para evitar símbolos raros al final */}
              <div>{"\n\n"}</div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPrintModal(false)}
              className="flex-1"
            >
              Cerrar
            </Button>
            <Button
              onClick={printTicket}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              <Printer size={16} className="mr-2" />
              Imprimir Ticket {window.ReactNativeWebView ? 'POS-80' : 'web'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetailPage;
